import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useXHR from '../../hooks/useXHR';
import { API } from '../../utils/api';
// XHR helper function outside component to avoid Emergent platform fetch interception
const makeXhrRequest = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    xhr.timeout = 15000;
    
    // Set headers
    if (options.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
    }
    
    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (e) {
          resolve(xhr.responseText);
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
      }
    };
    
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.ontimeout = () => reject(new Error('Request timeout'));
    xhr.send(options.body || null);
  });
};

const PayWhatYouWant = ({ showToast }) => {
  const { token, user } = useAuth();
  const { get, post, del: xhrDel, put } = useXHR(token);
  // Pre-populate tiers to avoid XHR blocking issues
  const [tiers, setTiers] = useState({
    "coffee": {"name": "Buy me a Coffee", "amount": 3.0, "emoji": "☕"},
    "lunch": {"name": "Buy me Lunch", "amount": 10.0, "emoji": "🍔"},
    "dinner": {"name": "Take me to Dinner", "amount": 25.0, "emoji": "🍽️"},
    "support": {"name": "Big Supporter", "amount": 50.0, "emoji": "🌟"},
    "patron": {"name": "InfoPilot Patron", "amount": 100.0, "emoji": "👑"}
  });
  const [selectedTier, setSelectedTier] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [recentDonations, setRecentDonations] = useState([]);
  const [stats, setStats] = useState({ total_raised: 0, total_donations: 0 });
  // Start with stripe enabled since we know it's configured
  const [stripeEnabled, setStripeEnabled] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);  // Start as not loading since we have hardcoded data

  // Optionally try to fetch updated stats in background (non-blocking)
  useEffect(() => {
    // Fire-and-forget background fetch for stats
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/stripe/donation-stats`, true);
    xhr.onload = function() {
      if (xhr.status === 200) {
        try {
          const data = JSON.parse(xhr.responseText);
          setStats(data);
        } catch (e) {
          console.log('Stats parse error, using defaults');
        }
      }
    };
    xhr.send();
  }, []);

  const handleDonate = async () => {
    if (!selectedTier && !customAmount) {
      showToast('Please select a donation amount', 'error');
      return;
    }

    if (customAmount) {
      const amount = parseFloat(customAmount);
      if (isNaN(amount) || amount < 1) {
        showToast('Minimum donation is $1.00', 'error');
        return;
      }
      if (amount > 500) {
        showToast('Maximum donation is $500.00', 'error');
        return;
      }
    }

    setLoading(true);
    try {
      const body = {
        origin_url: window.location.origin,
        message: message || undefined
      };

      if (selectedTier && !customAmount) {
        body.tier = selectedTier;
      } else if (customAmount) {
        body.custom_amount = parseFloat(customAmount);
      }

      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const data = await post('/stripe/create-donation-session', { body });

      if (data.url) {
        window.location.href = data.url;
      } else {
        showToast(data.detail || 'Failed to create donation session', 'error');
      }
    } catch (e) {
      showToast('Failed to process donation', 'error');
    }
    setLoading(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (dataLoading || stripeEnabled === null) {
    return (
      <div style={{
        padding: 30,
        textAlign: 'center',
        background: '#475569',
        borderRadius: 16,
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div style={{ fontSize: '2rem', marginBottom: 10, animation: 'pulse 1.5s infinite' }}>💜</div>
        <p style={{ color: '#a1a1aa' }}>Loading donation options...</p>
      </div>
    );
  }

  if (!stripeEnabled) {
    return (
      <div style={{
        padding: 30,
        textAlign: 'center',
        background: '#475569',
        borderRadius: 16,
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        <h3 style={{ color: '#047857', marginBottom: 10 }}>Donations Coming Soon</h3>
        <p style={{ color: '#a1a1aa' }}>Payment processing is being configured.</p>
      </div>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(252, 211, 77, 0.1), rgba(253, 230, 138, 0.1))',
      borderRadius: 16,
      padding: 24,
      border: '1px solid rgba(252, 211, 77, 0.3)'
    }} data-testid="pay-what-you-want">
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <h2 style={{
          fontSize: '1.8rem',
          background: 'linear-gradient(135deg, #047857, #0F172A)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: 8
        }}>
          Support InfoPilot Explorer
        </h2>
        <p style={{ color: '#a1a1aa', fontSize: '0.95rem' }}>
          Your contribution helps us build better search tools for everyone
        </p>
      </div>

      {/* Stats Banner */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 30,
        padding: '16px 20px',
        background: 'rgba(16, 185, 129, 0.1)',
        borderRadius: 12,
        marginBottom: 24,
        border: '1px solid rgba(16, 185, 129, 0.2)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>
            {formatCurrency(stats.total_raised)}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6ee7b7' }}>Total Raised</div>
        </div>
        <div style={{ width: 1, background: 'rgba(16, 185, 129, 0.3)' }} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>
            {stats.total_donations}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6ee7b7' }}>Supporters</div>
        </div>
      </div>

      {/* Donation Tiers */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ color: '#047857', fontWeight: 600, marginBottom: 12, display: 'block' }}>
          Choose an Amount
        </label>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12
        }}>
          {Object.entries(tiers).map(([key, tier]) => (
            <button
              key={key}
              onClick={() => {
                setSelectedTier(key);
                setCustomAmount('');
              }}
              style={{
                padding: '16px 12px',
                borderRadius: 12,
                border: selectedTier === key && !customAmount
                  ? '2px solid #047857'
                  : '1px solid rgba(255,255,255,0.1)',
                background: selectedTier === key && !customAmount
                  ? 'rgba(252, 211, 77, 0.2)'
                  : '#475569',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'center'
              }}
              data-testid={`donation-tier-${key}`}
            >
              <div style={{ fontSize: '1.8rem', marginBottom: 4 }}>{tier.emoji}</div>
              <div style={{ color: '#334155', fontWeight: 600, fontSize: '0.9rem' }}>
                {formatCurrency(tier.amount)}
              </div>
              <div style={{ color: '#a1a1aa', fontSize: '0.75rem', marginTop: 2 }}>
                {tier.name}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Amount */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ color: '#047857', fontWeight: 600, marginBottom: 8, display: 'block' }}>
          Or Enter Custom Amount
        </label>
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute',
            left: 14,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#047857',
            fontWeight: 600,
            fontSize: '1.1rem'
          }}>$</span>
          <input
            type="number"
            min="1"
            max="500"
            step="0.01"
            value={customAmount}
            onChange={(e) => {
              setCustomAmount(e.target.value);
              if (e.target.value) setSelectedTier(null);
            }}
            placeholder="Enter amount (1-500)"
            style={{
              width: '100%',
              padding: '14px 14px 14px 30px',
              borderRadius: 10,
              border: customAmount 
                ? '2px solid #047857' 
                : '1px solid rgba(255,255,255,0.1)',
              background: '#475569',
              color: '#0F172A',
              fontSize: '1.1rem',
              outline: 'none'
            }}
            data-testid="custom-amount-input"
          />
        </div>
      </div>

      {/* Optional Message */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ color: '#a1a1aa', fontSize: '0.85rem', marginBottom: 6, display: 'block' }}>
          Leave a message (optional)
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Thanks for building this amazing tool!"
          maxLength={200}
          rows={2}
          style={{
            width: '100%',
            padding: 12,
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.1)',
            background: '#475569',
            color: '#0F172A',
            fontSize: '0.9rem',
            resize: 'none',
            outline: 'none'
          }}
          data-testid="donation-message-input"
        />
        <div style={{ textAlign: 'right', fontSize: '0.7rem', color: '#71717a', marginTop: 4 }}>
          {message.length}/200
        </div>
      </div>

      {/* Donate Button */}
      <button
        onClick={handleDonate}
        disabled={loading || (!selectedTier && !customAmount)}
        style={{
          width: '100%',
          padding: '16px 24px',
          borderRadius: 12,
          border: 'none',
          background: loading || (!selectedTier && !customAmount)
            ? 'rgba(100, 100, 100, 0.3)'
            : 'linear-gradient(135deg, #FCD34D, #FDE68A)',
          color: '#0F172A',
          fontSize: '1.1rem',
          fontWeight: 700,
          cursor: loading || (!selectedTier && !customAmount) ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10
        }}
        data-testid="donate-button"
      >
        {loading ? (
          <>
            <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
            Processing...
          </>
        ) : (
          <>
            <span>💜</span>
            Donate {selectedTier && !customAmount 
              ? formatCurrency(tiers[selectedTier]?.amount || 0)
              : customAmount 
                ? formatCurrency(parseFloat(customAmount) || 0)
                : ''}
          </>
        )}
      </button>

      {/* Security Note */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        marginTop: 16,
        color: '#71717a',
        fontSize: '0.75rem'
      }}>
        <span>🔒</span>
        <span>Secure payment powered by Stripe</span>
      </div>

      {/* Recent Donations */}
      {recentDonations.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h4 style={{ color: '#047857', fontSize: '0.9rem', marginBottom: 12 }}>
            Recent Supporters
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {recentDonations.map((donation, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 14px',
                  background: 'rgba(255, 255, 255, 0.4)',
                  borderRadius: 8,
                  border: '1px solid rgba(255, 255, 255, 0.85)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: '1.2rem' }}>
                    {tiers[donation.tier]?.emoji || '💜'}
                  </span>
                  <div>
                    <div style={{ color: '#334155', fontSize: '0.85rem', fontWeight: 500 }}>
                      {donation.username || 'Anonymous'}
                    </div>
                    {donation.message && (
                      <div style={{ color: '#71717a', fontSize: '0.75rem', marginTop: 2 }}>
                        "{donation.message.substring(0, 50)}{donation.message.length > 50 ? '...' : ''}"
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#10b981', fontWeight: 600, fontSize: '0.9rem' }}>
                    {formatCurrency(donation.amount)}
                  </div>
                  <div style={{ color: '#71717a', fontSize: '0.7rem' }}>
                    {formatDate(donation.completed_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Info */}
      {user && (
        <div style={{
          marginTop: 20,
          padding: 12,
          background: 'rgba(245, 158, 11, 0.1)',
          borderRadius: 10,
          border: '1px solid rgba(245, 158, 11, 0.2)',
          textAlign: 'center'
        }}>
          <p style={{ color: '#93c5fd', fontSize: '0.8rem', margin: 0 }}>
            Donating as <strong>{user.username}</strong> • You'll earn XP points for your contribution!
          </p>
        </div>
      )}
    </div>
  );
};

// Donation Success Component
export const DonationSuccess = ({ showToast }) => {
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const checkPaymentStatus = useCallback(async (sessionId) => {
    try {
      const data = await get(`/stripe/checkout/status/${sessionId}`);
      
      setPaymentData(data);
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        return true;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return true;
      }
      
      return false;
    } catch (e) {
      console.error('Failed to check payment status:', e);
      return false;
    }
  }, [get]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      return;
    }

    // Polling for payment status
    const pollPayment = async () => {
      const completed = await checkPaymentStatus(sessionId);
      if (!completed && pollCount < 10) {
        setPollCount(prev => prev + 1);
        setTimeout(pollPayment, 2000);
      } else if (!completed) {
        setStatus('timeout');
      }
    };

    pollPayment();
  }, [checkPaymentStatus, pollCount]);

  if (status === 'checking') {
    return (
      <div style={{
        padding: 40,
        textAlign: 'center',
        background: '#475569',
        borderRadius: 16
      }} data-testid="donation-checking">
        <div style={{ fontSize: '3rem', marginBottom: 16, animation: 'pulse 1.5s infinite' }}>
          ⏳
        </div>
        <h3 style={{ color: '#047857', marginBottom: 8 }}>Verifying your donation...</h3>
        <p style={{ color: '#a1a1aa' }}>Please wait while we confirm your payment.</p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div style={{
        padding: 40,
        textAlign: 'center',
        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(252, 211, 77, 0.1))',
        borderRadius: 16,
        border: '1px solid rgba(16, 185, 129, 0.3)'
      }} data-testid="donation-success">
        <div style={{ fontSize: '4rem', marginBottom: 16 }}>🎉</div>
        <h2 style={{
          color: '#10b981',
          fontSize: '1.8rem',
          marginBottom: 12
        }}>
          Thank You for Your Support!
        </h2>
        <p style={{ color: '#a1a1aa', fontSize: '1.1rem', marginBottom: 20 }}>
          Your donation of <strong style={{ color: '#10b981' }}>
            ${paymentData?.amount_total?.toFixed(2) || '0.00'}
          </strong> has been received.
        </p>
        <div style={{
          padding: 16,
          background: 'rgba(16, 185, 129, 0.1)',
          borderRadius: 12,
          marginBottom: 20
        }}>
          <p style={{ color: '#6ee7b7', margin: 0 }}>
            You've earned <strong>{Math.round((paymentData?.amount_total || 0) * 10)} XP</strong> for your contribution!
          </p>
        </div>
        <button
          onClick={() => window.location.href = '/'}
          style={{
            padding: '12px 32px',
            borderRadius: 10,
            border: 'none',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            color: '#0F172A',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer'
          }}
          data-testid="return-home-btn"
        >
          Return to InfoPilot
        </button>
      </div>
    );
  }

  if (status === 'expired' || status === 'timeout') {
    return (
      <div style={{
        padding: 40,
        textAlign: 'center',
        background: 'rgba(245, 158, 11, 0.1)',
        borderRadius: 16,
        border: '1px solid rgba(245, 158, 11, 0.3)'
      }} data-testid="donation-expired">
        <div style={{ fontSize: '3rem', marginBottom: 16 }}>⏰</div>
        <h3 style={{ color: '#f59e0b', marginBottom: 12 }}>Session Expired</h3>
        <p style={{ color: '#a1a1aa', marginBottom: 20 }}>
          Your payment session has expired or timed out. Please try again.
        </p>
        <button
          onClick={() => window.location.href = '/donation'}
          style={{
            padding: '12px 32px',
            borderRadius: 10,
            border: 'none',
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            color: '#0F172A',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div style={{
      padding: 40,
      textAlign: 'center',
      background: 'rgba(239, 68, 68, 0.1)',
      borderRadius: 16,
      border: '1px solid rgba(239, 68, 68, 0.3)'
    }} data-testid="donation-error">
      <div style={{ fontSize: '3rem', marginBottom: 16 }}>❌</div>
      <h3 style={{ color: '#ef4444', marginBottom: 12 }}>Something went wrong</h3>
      <p style={{ color: '#a1a1aa', marginBottom: 20 }}>
        We couldn't verify your payment. Please contact support if you believe this is an error.
      </p>
      <button
        onClick={() => window.location.href = '/donation'}
        style={{
          padding: '12px 32px',
          borderRadius: 10,
          border: 'none',
          background: 'linear-gradient(135deg, #FCD34D, #FDE68A)',
          color: '#0F172A',
          fontSize: '1rem',
          fontWeight: 600,
          cursor: 'pointer'
        }}
      >
        Return to Donation Page
      </button>
    </div>
  );
};

export default PayWhatYouWant;
